import {
  Card,
  CardBody,
  Link,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { GetOrderDetailsAPI } from '../../../api/oms/order-details';
import { GetDeliveryTrackerInfo } from '../../../hooks/delivery-tracker-hooks';
import {
  usePurchaseHistoryContext,
  useExperienceContext,
  useCommonContext,
} from '../../../hooks/useContexts';
import { CallOrigin, ILineItem } from '../../../models/common';
import {
  IFulfillmentGroup,
  IOrderDetailsResponse,
  IOverrideAttribute,
} from '../../../models/order-details';
import { IRecord } from '../../../models/purchase-history';
import { generateImageUrl, routeBuilder } from '../../../utils/general';
import { ProgressiveImage } from '../../shared/ProgressiveImage';
import { retrieveSelectedOrderDetail } from '../../../utils/purchase-history/helpers';
import { pushOrderDetailsOrderCardEvent } from '../../../utils/purchase-history/analytics';
import { separateReturnedFG } from '../../../utils/returns/returns-fg-helper';
import { dateFormat } from '../../order-details/util/format';
import { PHOrderDetailsAndActions } from '../delivery-tracker/PHOrderDetailsAndActions';
import { CardHeader } from './CardHeader';
import { PHCardDetailSkeleton } from './CardSkeletonLoader';

import './PHCard.scss';

export interface IPHCard {
  customerType: string;
  record: IRecord;
  customerRole?: string;
  index: number;
}

const getOrderType = (
  orderNumber: string | undefined,
): 'ONLINE' | 'INSTORE' => {
  if (orderNumber && (orderNumber[0] === 'W' || orderNumber[0] === 'C')) {
    return 'ONLINE';
  }
  return 'INSTORE';
};

const generateOrderDetailsRequest = (
  order: IRecord,
  customerType: string,
  svocID: string,
  requestOriginatedFrom: CallOrigin,
  timezone: string,
) => {
  const {
    transactionType,
    transactionId,
    registerNumber,
    salesDate,
    storeNumber,
    orderNumber,
    orderNumbers,
    accountIdValue,
    accountIdType,
  } = order;

  const filteredOrderNumber = orderNumber ?? orderNumbers?.[0];
  const orderType = getOrderType(filteredOrderNumber);

  const overrideAttributes: IOverrideAttribute[] = [];
  if (accountIdValue) {
    overrideAttributes.push({ name: 'accountIdValue', value: accountIdValue });
    if (accountIdType)
      overrideAttributes.push({ name: 'accountIdType', value: accountIdType });
  }
  const overrideAttributesList: IOverrideAttribute[] | undefined =
    overrideAttributes.length > 0 ? overrideAttributes : undefined;

  if (customerType === 'b2b' && orderType === 'ONLINE') {
    if (transactionType === 'R') {
      return {
        orderDetailsRequest: {
          searchType: 'online',
          registerNumber: registerNumber ?? 0,
          salesDate: salesDate ?? '',
          storeNumber: storeNumber ?? 0,
          transactionId: transactionId ?? 0,
          transactionType: transactionType ?? '',
          orderNumber: filteredOrderNumber ?? '',
          customerAccountId: svocID,
          groupAndSortBy: {
            groupBy: 'fulfillmentType',
            filterResponse: true,
          },
          requestOriginatedFrom,
          timezone,
          ...(overrideAttributesList && { overrideAttributesList }),
        },
      };
    }
    return {
      orderDetailsRequest: {
        searchType: 'online',
        orderNumber: filteredOrderNumber ?? '',
        customerAccountId: svocID,
        groupAndSortBy: {
          groupBy: 'fulfillmentType',
          filterResponse: true,
        },
        requestOriginatedFrom,
        timezone,
        ...(overrideAttributesList && { overrideAttributesList }),
      },
    };
  }
  if (orderType === 'INSTORE') {
    return {
      orderDetailsRequest: {
        searchType: 'instore',
        registerNumber: registerNumber ?? 0,
        salesDate: salesDate ?? '',
        storeNumber: storeNumber ?? 0,
        transactionId: transactionId ?? 0,
        orderNumber: filteredOrderNumber ?? '',
        customerAccountId: svocID,
        groupAndSortBy: {
          groupBy: 'fulfillmentType',
          filterResponse: true,
        },
        requestOriginatedFrom,
        timezone,
        ...(overrideAttributesList && { overrideAttributesList }),
      },
    };
  }

  return {
    orderDetailsRequest: {
      searchType: orderType?.toLowerCase(),
      orderNumber: filteredOrderNumber ?? '',
      requestOriginatedFrom,
      timezone,
      ...(overrideAttributesList && { overrideAttributesList }),
    },
  };
};

export const orderDetailView = (
  orderDetails: IOrderDetailsResponse | null,
  orderDetailsLoaded: boolean,
  record: IRecord,
) => {
  if (!orderDetailsLoaded) {
    return (
      <>
        <PHCardDetailSkeleton numberOfSkeletons={1} />
      </>
    );
  }

  if (orderDetails) {
    const { orderedGroups } = separateReturnedFG(
      orderDetails.fulfillmentGroups,
    );

    const orderNumber = record?.orderNumber ?? record?.orderNumbers?.[0] ?? '';

    const canceledLineItems = orderedGroups.flatMap((fg) =>
      fg.lineItems && fg.cancellationStatus !== 'OUT_OF_STOCK'
        ? fg.lineItems.filter((item) => item.statusDescription === 'Canceled')
        : [],
    );

    return (
      <div>
        {orderedGroups?.map((fg: IFulfillmentGroup, indexFG: number) => {
          if (
            fg.lineItems &&
            fg.cancellationStatus !== 'OUT_OF_STOCK' &&
            fg.lineItems.every((item) => item.statusDescription === 'Canceled')
          ) {
            return null;
          }
          return (
            <div className={`${indexFG > 0 ? 'sui-mt-8' : ''}`}>
              <PHOrderDetailsAndActions
                emailId={orderDetails?.emailId}
                fulfillmentGroup={fg}
                net={orderDetails?.net || ''}
                orderDetailsLoaded={orderDetailsLoaded}
                orderNumber={orderNumber}
                salesDate={record?.salesDate}
                variant="card"
                isPTCOrder={orderDetails?.isPTCOrder}
              />
            </div>
          );
        })}
        {canceledLineItems.length > 0 && (
          <div className="sui-flex sui-w-full sui-flex-col sui-gap-0 sui-min-w-[330px] md:sui-min-w-[422px] sui-mt-2">
            <div className="sui-flex sui-flex-col">
              <Typography
                variant="body-base"
                weight="bold"
                style={{
                  textTransform: 'capitalize',
                  marginBottom: '12px',
                }}
              >
                {`Canceled ${dateFormat(
                  canceledLineItems?.[0]?.statusUpdatedDate,
                  'MMMM d',
                )}`}
              </Typography>
            </div>

            <div>
              <div
                className="sui-flex sui-items-center sui-relative sui-gap-[16px]"
                style={{
                  width: '48px',
                  height: '48px',
                }}
              >
                {canceledLineItems.map((lineItem: ILineItem, idx: number) => {
                  if (
                    (lineItem as ILineItem)?.parentLine?.relationType ===
                    'COMBO'
                  )
                    return null;

                  return (
                    <div
                      key={idx}
                      style={{
                        overflow: 'hidden',
                        minWidth: '48px',
                        height: '48px',
                      }}
                    >
                      <ProgressiveImage
                        src={generateImageUrl(lineItem) ?? ''}
                        alt={
                          (lineItem as ILineItem)?.description ??
                          'Unavailable image'
                        }
                        width={48}
                        height={48}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

export const PHCard = ({
  customerRole,
  customerType,
  record,
  index,
}: IPHCard) => {
  const { allOrderDetails, setAllOrderDetails, cpsDataSource } =
    usePurchaseHistoryContext();
  const { channel } = useExperienceContext();
  const { timezone } = useCommonContext();
  // TODO - needs to be used or removed. i.e. any error handling UI, etc.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [orderDetailsError, setOrderDetailsError] = useState<AxiosError | null>(
    null,
  );
  const [orderDetailsLoaded, setOrderDetailsLoaded] = useState<boolean>(false);
  const [isLoadingOrderDetails, setIsLoadingOrderDetails] =
    useState<boolean>(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { svocID, userID } = useThdCustomer();

  const {
    registerNumber,
    orderNumber,
    orderNumbers,
    POJobName,
    projectName,
    receiptDetails,
    returnTotal,
    salesDate,
    totalAmount,
    transactionId,
    transactionIdWithCheckDigit,
  } = record;

  const selectedOrderDetail = retrieveSelectedOrderDetail(
    record,
    allOrderDetails,
    customerType,
    cpsDataSource,
  );

  useEffect(() => {
    if (selectedOrderDetail) {
      setOrderDetailsLoaded(true);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          !orderDetailsLoaded &&
          !selectedOrderDetail &&
          !isLoadingOrderDetails
        ) {
          setIsLoadingOrderDetails(true);
          GetOrderDetailsAPI(
            customerType,
            customerRole || '',
            generateOrderDetailsRequest(
              record,
              customerType,
              svocID,
              'purchaseHistory',
              timezone,
            ),
            channel,
            userID,
          )
            .then((response: AxiosResponse<IOrderDetailsResponse>) => {
              GetDeliveryTrackerInfo(response.data, userID).then(
                (deliveryTrackingData) => {
                  setAllOrderDetails((prevDetails) =>
                    deliveryTrackingData
                      ? [
                          ...prevDetails,
                          deliveryTrackingData as IOrderDetailsResponse,
                        ]
                      : prevDetails,
                  );
                },
              );
            })
            .catch((error: AxiosError) => {
              setOrderDetailsError(error);
            })
            .finally(() => {
              setOrderDetailsLoaded(true);
              setIsLoadingOrderDetails(false);
            });
        }
      },
      { rootMargin: '0px 0px 400px 0px', threshold: 1 },
    );
    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }
    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [orderDetailsLoaded, isLoadingOrderDetails, selectedOrderDetail]);

  const orderDetailsLink = `/myaccount/order-details${routeBuilder({
    index,
    customerType,
    ...record,
  })}`;

  const handleCardClick = () => {
    pushOrderDetailsOrderCardEvent();
  };

  return (
    <div className="sui-mb-[24px] card-edits">
      <div ref={sentinelRef} data-testid="sentinel-ref" />
      <Link
        href={orderDetailsLink}
        onClick={handleCardClick}
        data-testid="order-details-link"
      >
        <Card initialSize="full">
          <CardHeader
            isRefund={(returnTotal ?? 0) > 0}
            orderNumber={orderNumbers?.[0] || orderNumber || receiptDetails}
            POJobName={POJobName || ''}
            projectName={projectName}
            registerNumber={registerNumber}
            salesDate={salesDate}
            totalAmount={totalAmount}
            transactionId={transactionIdWithCheckDigit || transactionId}
            cpsDataSource={cpsDataSource}
            statusDescription={selectedOrderDetail?.statusDescription}
          />
          <CardBody>
            <div>
              {orderDetailView(selectedOrderDetail, orderDetailsLoaded, record)}
            </div>
          </CardBody>
        </Card>
      </Link>
    </div>
  );
};
